// @ts-nocheck
import * as React from "react";
import { TenantSwitcher } from "@widgets/layout/TenantSwitcher";
import { AccountMenu } from "@widgets/layout/AccountMenu";
import { Menu, Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@widgets/layout/LanguageSwitcher";
import { GlobalSearch } from "./GlobalSearch";

import { loadSettings } from "@features/settings/api/settings";
import { useQuery } from "@tanstack/react-query";
import { StorizoneInteractiveLogo } from "@ui/core";
import { useAuth } from "@app/providers/auth";
import { useTenant } from "@app/providers/tenant";
import { AdminNotificationBell } from "@features/notifications/components/AdminNotificationBell";

type AdminTheme = "light" | "dark";

function getInitialTheme(): AdminTheme {
  if (typeof window === "undefined") return "light";
  const docTheme = document.documentElement.getAttribute("data-theme");
  if (docTheme === "dark" || docTheme === "light") return docTheme;
  const value = window.localStorage.getItem("storizone:theme");
  if (value === "dark" || value === "light") return value;
  if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
}

function ThemeToggle({
  theme,
  onThemeChange,
}: {
  theme: AdminTheme;
  onThemeChange: (theme: AdminTheme) => void;
}) {
  const { t } = useTranslation();
  const Icon = theme === "light" ? Sun : Moon;
  return (
    <button
      type="button"
      aria-label={t("toggleTheme")}
      className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-primary/50 text-foreground"
      onClick={() => onThemeChange(theme === "light" ? "dark" : "light")}
    >
      <Icon size={18} />
    </button>
  );
}

export function Topbar({
  isSidebarOpen,
  onToggleSidebar,
  children,
}: {
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [theme, setTheme] = React.useState<AdminTheme>(getInitialTheme);
  const enableSearch = !!import.meta.env.VITE_ENABLE_GLOBAL_SEARCH;
  const { getToken, refreshToken } = useAuth();
  const { tenantId } = useTenant();
  const token = getToken();
  const hasRecoverableSession = Boolean(token || refreshToken);

  // Fetch settings to get logo
  const { data: settings } = useQuery({
    queryKey: ["admin.settings", tenantId ?? "no-tenant"],
    queryFn: () => loadSettings(),
    enabled: hasRecoverableSession,
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  });

  const displayName = settings?.general?.storeName || null;

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.classList.toggle("dark", theme === "dark");
    document.documentElement.classList.toggle("light", theme === "light");
    try {
      window.localStorage.setItem("storizone:theme", theme);
    } catch {}
  }, [theme]);

  return (
    <header className="sticky top-0 z-20 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="mx-auto flex h-14 w-full max-w-screen-2xl items-center justify-between px-2 sm:px-4">
        <div className="flex min-w-0 shrink-0 items-center gap-1 sm:gap-2">
          <button
            type="button"
            aria-label={t("toggleNavigation")}
            aria-controls="admin-sidebar"
            aria-expanded={isSidebarOpen}
            onClick={onToggleSidebar}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            <Menu size={18} />
          </button>

          <div className="ml-1 flex h-9 w-9 shrink-0 items-center overflow-hidden lg:ml-2 lg:w-[146px]">
            <StorizoneInteractiveLogo
              variant="lockup"
              theme={theme}
              width={146}
              className="shrink-0"
            />
          </div>
        </div>
        <div className="flex min-w-0 flex-1 items-center justify-center px-1 sm:px-2">
          {enableSearch ? <GlobalSearch /> : children}
        </div>
        <nav className="flex shrink-0 items-center gap-1 text-sm sm:gap-2">
          <TenantSwitcher />
          <LanguageSwitcher />
          <AdminNotificationBell />
          <ThemeToggle theme={theme} onThemeChange={setTheme} />
          <AccountMenu storeName={displayName} />
        </nav>
      </div>
    </header>
  );
}
